import React, { useState, useEffect, useCallback } from 'react';
import './ARPage.css';
import { formatPatientName } from '../utils/formatName';
import { formatCurrency } from '../utils/formatCurrency';
import PatientARClaimsTable from '../components/PatientARClaimsTable';
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from '../utils/dateInputBounds';

const API_URL = process.env.REACT_APP_API_URL || '';

function formatCompact(val) {
  const n = Number(val);
  if (!n || n < 1) return '$0';
  if (n >= 1000) return '$' + (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'K';
  return '$' + n.toFixed(0);
}

function ARPage({ selectedPractice, readOnly = false }) {
  const [activeTab, setActiveTab] = useState('insurance');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Insurance tab state
  const [insuranceData, setInsuranceData] = useState([]);
  const [insuranceLoading, setInsuranceLoading] = useState(false);
  const [insuranceSort, setInsuranceSort] = useState({ field: 'outstanding_balance', order: 'desc' });

  // Patient tab state
  const [patientData, setPatientData] = useState([]);
  const [patientLoading, setPatientLoading] = useState(false);
  const [patientSort, setPatientSort] = useState({ field: 'total_balance', order: 'desc' });
  const [expandedPatientId, setExpandedPatientId] = useState(null);
  const [patientClaims, setPatientClaims] = useState({});

  // Notify modal state
  const [showNotifyModal, setShowNotifyModal] = useState(false);
  const [notifyPatient, setNotifyPatient] = useState(null);
  const [notifyMethod, setNotifyMethod] = useState('email');
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifySending, setNotifySending] = useState(false);

  // Enrich state (replaces card sync)
  const [enriching, setEnriching] = useState(false);

  // Card filter: 'all' | 'yes' | 'no'
  const [cardFilter, setCardFilter] = useState('all');

  // Billing state
  const [selectedPatients, setSelectedPatients] = useState(new Set());
  const [showBillModal, setShowBillModal] = useState(false);
  const [billing, setBilling] = useState(false);
  const [billResults, setBillResults] = useState(null); // null | { total, succeeded, failed, total_charged, results }

  // Messages
  const [message, setMessage] = useState(null);

  const practiceId = selectedPractice?.id;

  // Fetch insurance data
  const fetchInsuranceData = useCallback(async () => {
    if (!practiceId) return;
    setInsuranceLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const resp = await fetch(`${API_URL}/api/practices/${practiceId}/ar/insurance?${params}`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Failed to load insurance A/R');
      const json = await resp.json();
      setInsuranceData(json.data || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setInsuranceLoading(false);
    }
  }, [practiceId, dateFrom, dateTo]);

  // Fetch patient data
  const fetchPatientData = useCallback(async () => {
    if (!practiceId) return;
    setPatientLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set('date_from', dateFrom);
      if (dateTo) params.set('date_to', dateTo);
      const resp = await fetch(`${API_URL}/api/practices/${practiceId}/ar/patients?${params}`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Failed to load patient A/R');
      const json = await resp.json();
      setPatientData(json.data || []);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setPatientLoading(false);
    }
  }, [practiceId, dateFrom, dateTo]);

  // Fetch both datasets so tab badges always have totals
  useEffect(() => {
    fetchInsuranceData();
    fetchPatientData();
  }, [fetchInsuranceData, fetchPatientData]);

  // Fetch claims for expanded patient row — uses patient_id if available, else patient_name
  const fetchPatientClaims = useCallback(async (patient) => {
    const key = patient.patient_name;
    if (!practiceId || patientClaims[key]) return;
    try {
      const params = new URLSearchParams();
      params.set('patient_name', patient.patient_name);
      const resp = await fetch(`${API_URL}/api/practices/${practiceId}/ar/patients/claims?${params}`, { credentials: 'include' });
      if (!resp.ok) throw new Error('Failed to load claims');
      const json = await resp.json();
      setPatientClaims(prev => ({ ...prev, [key]: json.data || [] }));
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }, [practiceId, patientClaims]);

  // Toggle expanded row — keyed by patient_name
  const togglePatientRow = (patient) => {
    const key = patient.patient_name;
    if (expandedPatientId === key) {
      setExpandedPatientId(null);
    } else {
      setExpandedPatientId(key);
      fetchPatientClaims(patient);
    }
  };

  // Sorting
  const sortData = (data, sort) => {
    return [...data].sort((a, b) => {
      let aVal = a[sort.field];
      let bVal = b[sort.field];
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      if (aVal < bVal) return sort.order === 'asc' ? -1 : 1;
      if (aVal > bVal) return sort.order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleInsuranceSort = (field) => {
    setInsuranceSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc',
    }));
  };

  const handlePatientSort = (field) => {
    setPatientSort(prev => ({
      field,
      order: prev.field === field && prev.order === 'desc' ? 'asc' : 'desc',
    }));
  };

  const sortArrow = (sort, field) => {
    if (sort.field !== field) return '';
    return sort.order === 'asc' ? ' ↑' : ' ↓';
  };

  // Enrich patients (phone, email, cards from EMR)
  const handleEnrich = async () => {
    if (!practiceId) return;
    setEnriching(true);
    setMessage(null);
    try {
      const resp = await fetch(`${API_URL}/api/practices/${practiceId}/ar/enrich-patients`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!resp.ok) {
        const json = await resp.json();
        throw new Error(json.error || 'Enrichment failed');
      }
      const json = await resp.json();
      const r = json.data;
      const parts = [`${r.enriched} updated`];
      if (r.resolved) parts.push(`${r.resolved} EMR IDs resolved`);
      if (r.skipped) parts.push(`${r.skipped} skipped`);
      if (r.errors) parts.push(`${r.errors} errors`);
      setMessage({ type: 'success', text: `Enrichment complete: ${parts.join(', ')}.` });
      fetchPatientData();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setEnriching(false);
    }
  };

  // Billing — checkbox helpers
  const billablePatients = patientData.filter(r => r.has_card_on_file && r.patient_id);
  const filteredPatients = patientData.filter(row =>
    cardFilter === 'all' ? true :
    cardFilter === 'yes' ? row.has_card_on_file :
    !row.has_card_on_file
  );

  const togglePatientSelection = (patientId) => {
    setSelectedPatients(prev => {
      const next = new Set(prev);
      if (next.has(patientId)) next.delete(patientId);
      else next.add(patientId);
      return next;
    });
  };

  const toggleAllBillable = () => {
    const visibleBillable = filteredPatients.filter(r => r.has_card_on_file && r.patient_id);
    const allSelected = visibleBillable.every(r => selectedPatients.has(r.patient_id));
    if (allSelected) {
      setSelectedPatients(new Set());
    } else {
      setSelectedPatients(new Set(visibleBillable.map(r => r.patient_id)));
    }
  };

  const selectedForBilling = patientData.filter(r => selectedPatients.has(r.patient_id));
  const selectedTotal = selectedForBilling.reduce((sum, r) => sum + r.total_balance, 0);

  const handleBillSelected = async () => {
    if (!practiceId || selectedForBilling.length === 0) return;
    setBilling(true);
    setBillResults(null);
    setMessage(null);
    try {
      const resp = await fetch(`${API_URL}/api/practices/${practiceId}/ar/bill-patients`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          patients: selectedForBilling.map(p => ({
            patient_id: p.patient_id,
            amount: p.total_balance,
          })),
        }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error || 'Billing failed');
      setBillResults(json);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
      setShowBillModal(false);
    } finally {
      setBilling(false);
    }
  };

  const closeBillModal = () => {
    setShowBillModal(false);
    setBillResults(null);
    setSelectedPatients(new Set());
    fetchPatientData();
  };

  // Notify modal
  const openNotifyModal = (patient) => {
    setNotifyPatient(patient);
    setNotifyEmail(patient.email || '');
    setNotifyMethod('email');
    setNotifyMessage('');
    setShowNotifyModal(true);
  };

  const handleSendNotification = async () => {
    if (!practiceId || !notifyPatient) return;
    setNotifySending(true);
    try {
      const resp = await fetch(`${API_URL}/api/practices/${practiceId}/ar/patients/${notifyPatient.patient_id}/notify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          method: notifyMethod,
          message: notifyMessage || undefined,
        }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error || json.message || 'Failed to send notification');
      setMessage({ type: 'success', text: json.message });
      setShowNotifyModal(false);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setNotifySending(false);
    }
  };

  // Summary totals
  const insuranceTotals = insuranceData.reduce(
    (acc, row) => ({
      claims: acc.claims + row.claim_count,
      billed: acc.billed + row.total_billed,
      paid: acc.paid + row.total_paid,
      adjustments: acc.adjustments + row.total_adjustments,
      outstanding: acc.outstanding + row.outstanding_balance,
    }),
    { claims: 0, billed: 0, paid: 0, adjustments: 0, outstanding: 0 }
  );

  const patientTotals = patientData.reduce(
    (acc, row) => ({
      patients: acc.patients + 1,
      claims: acc.claims + row.claim_count,
      balance: acc.balance + row.total_balance,
    }),
    { patients: 0, claims: 0, balance: 0 }
  );

  if (!selectedPractice) {
    return <div className="ar-page"><p>Select a practice to view A/R.</p></div>;
  }

  return (
    <div className="ar-page">
      <div className="ar-header">
        <h1 className="page-title">Accounts Receivable</h1>
      </div>

      {/* Message */}
      {message && (
        <div className={`ar-message ${message.type}`}>
          {message.text}
          <button className="ar-message-close" onClick={() => setMessage(null)}>×</button>
        </div>
      )}

      {/* Tabs */}
      <div className="detail-tabs">
        <button
          className={`tab-button ${activeTab === 'insurance' ? 'active' : ''}`}
          onClick={() => setActiveTab('insurance')}
        >
          Insurance
          {insuranceTotals.outstanding > 0 && (
            <span className="ar-tab-badge">{formatCompact(insuranceTotals.outstanding)}</span>
          )}
        </button>
        <button
          className={`tab-button ${activeTab === 'patients' ? 'active' : ''}`}
          onClick={() => setActiveTab('patients')}
        >
          Patients
          {patientTotals.balance > 0 && (
            <span className="ar-tab-badge">{formatCompact(patientTotals.balance)}</span>
          )}
        </button>
      </div>

      {/* Date Filters */}
      <div className="ar-filter-bar">
        <label className="ar-filter-label">From</label>
        <input
          type="date"
          min={DATE_INPUT_MIN}
          max={DATE_INPUT_MAX}
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
        />
        <label className="ar-filter-label">To</label>
        <input
          type="date"
          min={DATE_INPUT_MIN}
          max={DATE_INPUT_MAX}
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
        />
        {(dateFrom || dateTo) && (
          <button className="btn btn-sm" onClick={() => { setDateFrom(''); setDateTo(''); }}>
            Clear Dates
          </button>
        )}
        {!readOnly && activeTab === 'patients' && (
          <>
            <div className="ar-card-filter">
              <button
                className={`btn btn-sm ${cardFilter === 'all' ? 'btn-primary' : ''}`}
                onClick={() => setCardFilter('all')}
              >All</button>
              <button
                className={`btn btn-sm ${cardFilter === 'yes' ? 'btn-primary' : ''}`}
                onClick={() => setCardFilter('yes')}
              >CC on File</button>
              <button
                className={`btn btn-sm ${cardFilter === 'no' ? 'btn-primary' : ''}`}
                onClick={() => setCardFilter('no')}
              >No CC</button>
            </div>
            <button
              className="btn btn-sm ar-sync-btn"
              onClick={handleEnrich}
              disabled={enriching}
              title="Fetch phone, email, and card data from EMR for patients missing info."
            >
              {enriching ? 'Enriching...' : 'Enrich'}
            </button>
            {selectedPatients.size > 0 && (
              <button
                className="btn btn-sm btn-primary ar-bill-btn"
                onClick={() => setShowBillModal(true)}
              >
                Bill Selected ({selectedPatients.size}) — {formatCurrency(selectedTotal)}
              </button>
            )}
          </>
        )}
      </div>

      {/* Insurance Tab */}
      {activeTab === 'insurance' && (
        <div className="ar-tab-content">
          {insuranceLoading ? (
            <p className="ar-loading">Loading insurance A/R...</p>
          ) : insuranceData.length === 0 ? (
            <p className="ar-empty">No outstanding insurance balances found.</p>
          ) : (
            <div className="ar-table-wrapper">
              <table className="ar-table">
                <thead>
                  <tr>
                    <th className="sortable" onClick={() => handleInsuranceSort('payer_name')}>
                      Payer{sortArrow(insuranceSort, 'payer_name')}
                    </th>
                    <th className="sortable" onClick={() => handleInsuranceSort('claim_count')}>
                      # Claims{sortArrow(insuranceSort, 'claim_count')}
                    </th>
                    <th className="sortable col-currency" onClick={() => handleInsuranceSort('total_billed')}>
                      Billed{sortArrow(insuranceSort, 'total_billed')}
                    </th>
                    <th className="sortable col-currency" onClick={() => handleInsuranceSort('total_paid')}>
                      Paid{sortArrow(insuranceSort, 'total_paid')}
                    </th>
                    <th className="sortable col-currency" onClick={() => handleInsuranceSort('total_adjustments')}>
                      Adjustments{sortArrow(insuranceSort, 'total_adjustments')}
                    </th>
                    <th className="sortable col-currency" onClick={() => handleInsuranceSort('outstanding_balance')}>
                      Outstanding{sortArrow(insuranceSort, 'outstanding_balance')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortData(insuranceData, insuranceSort).map((row, i) => (
                    <tr key={i}>
                      <td className="col-name">{row.payer_name}</td>
                      <td>{row.claim_count}</td>
                      <td className="col-currency">{formatCurrency(row.total_billed)}</td>
                      <td className="col-currency">{formatCurrency(row.total_paid)}</td>
                      <td className="col-currency">{formatCurrency(row.total_adjustments)}</td>
                      <td className="col-currency col-outstanding">{formatCurrency(row.outstanding_balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Patient Tab */}
      {activeTab === 'patients' && (
        <div className="ar-tab-content">
          {patientLoading ? (
            <p className="ar-loading">Loading patient A/R...</p>
          ) : patientData.length === 0 ? (
            <p className="ar-empty">No outstanding patient balances found.</p>
          ) : (
            <div className="ar-table-wrapper">
              <table className="ar-table">
                <thead>
                  <tr>
                    {!readOnly && (
                      <th className="col-checkbox" onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={filteredPatients.filter(r => r.has_card_on_file && r.patient_id).length > 0 &&
                            filteredPatients.filter(r => r.has_card_on_file && r.patient_id).every(r => selectedPatients.has(r.patient_id))}
                          onChange={toggleAllBillable}
                          title="Select all billable patients"
                        />
                      </th>
                    )}
                    <th className="sortable" onClick={() => handlePatientSort('patient_name')}>
                      Patient{sortArrow(patientSort, 'patient_name')}
                    </th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th className="sortable" onClick={() => handlePatientSort('claim_count')}>
                      # Claims{sortArrow(patientSort, 'claim_count')}
                    </th>
                    <th className="sortable col-currency" onClick={() => handlePatientSort('total_balance')}>
                      Balance{sortArrow(patientSort, 'total_balance')}
                    </th>
                    {!readOnly && <th>CC on File</th>}
                    {!readOnly && <th className="col-actions">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {sortData(filteredPatients, patientSort).map((row) => (
                    <React.Fragment key={row.patient_name}>
                      <tr
                        className={`ar-row ${expandedPatientId === row.patient_name ? 'expanded' : ''}`}
                        onClick={() => togglePatientRow(row)}
                      >
                        {!readOnly && (
                          <td className="col-checkbox" onClick={e => e.stopPropagation()}>
                            {row.has_card_on_file && row.patient_id ? (
                              <input
                                type="checkbox"
                                checked={selectedPatients.has(row.patient_id)}
                                onChange={() => togglePatientSelection(row.patient_id)}
                              />
                            ) : null}
                          </td>
                        )}
                        <td className="col-name">{formatPatientName(row.patient_name)}</td>
                        <td className="col-mono">{row.phone || '-'}</td>
                        <td className="col-mono">{row.email || '-'}</td>
                        <td>{row.claim_count}</td>
                        <td className="col-currency col-outstanding">{formatCurrency(row.total_balance)}</td>
                        {!readOnly && (
                          <td>
                            {row.has_card_on_file ? (
                              <span
                                className={`ar-card-badge ${row.last_decline ? 'ar-card-declined' : 'ar-card-yes'}`}
                                title={row.last_decline ? `Declined: ${row.last_decline.decline_reason || 'Unknown'} (${row.last_decline.declined_at ? new Date(row.last_decline.declined_at).toLocaleDateString() : ''})` : ''}
                              >
                                {row.cards?.[0] ? `${row.cards[0].card_type} ****${row.cards[0].last4}` : 'Yes'}
                              </span>
                            ) : (
                              <span className="ar-card-badge ar-card-no">No</span>
                            )}
                          </td>
                        )}
                        {!readOnly && (
                          <td className="col-actions" onClick={e => e.stopPropagation()}>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => openNotifyModal(row)}
                              title="Send balance notification"
                            >
                              Notify
                            </button>
                          </td>
                        )}
                      </tr>
                      {expandedPatientId === row.patient_name && (
                        <tr className="ar-detail-row">
                          <td colSpan={readOnly ? 5 : 8}>
                            <div className="ar-detail-content">
                              <h4>Outstanding Claims</h4>
                              {!patientClaims[row.patient_name] ? (
                                <p className="ar-loading">Loading claims...</p>
                              ) : patientClaims[row.patient_name].length === 0 ? (
                                <p>No outstanding claims.</p>
                              ) : (
                                <PatientARClaimsTable claims={patientClaims[row.patient_name]} />
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Billing Confirmation / Results Modal */}
      {!readOnly && showBillModal && (
        <div className="modal-overlay" onClick={() => { if (!billing) closeBillModal(); }}>
          <div className="modal-content ar-bill-modal" onClick={e => e.stopPropagation()}>
            {!billResults ? (
              <>
                <h2>Confirm Billing</h2>
                <p className="ar-bill-summary">
                  Charge <strong>{selectedForBilling.length}</strong> patient{selectedForBilling.length !== 1 ? 's' : ''} for
                  a total of <strong>{formatCurrency(selectedTotal)}</strong>
                </p>
                <div className="ar-bill-table-wrapper">
                  <table className="ar-bill-table">
                    <thead>
                      <tr>
                        <th>Patient</th>
                        <th className="col-currency">Balance</th>
                        <th>Card</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedForBilling.map(p => (
                        <tr key={p.patient_id}>
                          <td className="col-name">{formatPatientName(p.patient_name)}</td>
                          <td className="col-currency col-outstanding">{formatCurrency(p.total_balance)}</td>
                          <td>
                            {p.cards?.[0]
                              ? `${p.cards[0].card_type} ****${p.cards[0].last4}`
                              : 'Card on file'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="modal-actions">
                  <button className="btn" onClick={closeBillModal} disabled={billing}>
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleBillSelected}
                    disabled={billing}
                  >
                    {billing ? 'Processing...' : `Confirm & Charge ${formatCurrency(selectedTotal)}`}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2>Billing Results</h2>
                <div className="ar-bill-result-summary">
                  <div className={`ar-bill-stat ${billResults.succeeded > 0 ? 'success' : ''}`}>
                    <span className="ar-bill-stat-num">{billResults.succeeded}</span>
                    <span className="ar-bill-stat-label">Charged</span>
                  </div>
                  {billResults.failed > 0 && (
                    <div className="ar-bill-stat error">
                      <span className="ar-bill-stat-num">{billResults.failed}</span>
                      <span className="ar-bill-stat-label">Failed</span>
                    </div>
                  )}
                  <div className="ar-bill-stat">
                    <span className="ar-bill-stat-num">{formatCurrency(billResults.total_charged)}</span>
                    <span className="ar-bill-stat-label">Total Charged</span>
                  </div>
                </div>
                <div className="ar-bill-table-wrapper">
                  <table className="ar-bill-table">
                    <thead>
                      <tr>
                        <th>Patient</th>
                        <th className="col-currency">Amount</th>
                        <th>Card</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billResults.results.map((r, i) => (
                        <tr key={i} className={r.success ? '' : r.declined ? 'ar-bill-row-declined' : 'ar-bill-row-failed'}>
                          <td className="col-name">{formatPatientName(r.patient_name)}</td>
                          <td className="col-currency">{formatCurrency(r.amount_charged || r.amount)}</td>
                          <td>{r.card_type && r.last4 ? `${r.card_type} ****${r.last4}` : '-'}</td>
                          <td>
                            {r.success ? (
                              <span className="ar-bill-status-ok">Charged</span>
                            ) : r.declined ? (
                              <span className="ar-bill-status-declined" title={r.decline_reason}>{r.decline_reason || 'Card Declined'}</span>
                            ) : (
                              <span className="ar-bill-status-fail" title={r.error}>{r.error || 'Failed'}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="modal-actions">
                  <button className="btn btn-primary" onClick={closeBillModal}>
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Notify Modal */}
      {!readOnly && showNotifyModal && notifyPatient && (
        <div className="modal-overlay" onClick={() => setShowNotifyModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>Notify Patient</h2>
            <p className="ar-notify-summary">
              <strong>{formatPatientName(notifyPatient.patient_name)}</strong> — Outstanding balance: <strong>{formatCurrency(notifyPatient.total_balance)}</strong>
            </p>

            <div className="ar-notify-method">
              <label className="ar-notify-method-label">Method:</label>
              <div className="ar-notify-method-options">
                <button
                  className={`btn btn-sm ${notifyMethod === 'email' ? 'btn-primary' : ''}`}
                  onClick={() => setNotifyMethod('email')}
                >
                  Email
                </button>
                <button
                  className="btn btn-sm"
                  disabled
                  title="SMS notifications coming soon"
                >
                  SMS (Coming soon)
                </button>
              </div>
            </div>

            <div className="ar-notify-field">
              <label>Email Address</label>
              <input
                type="email"
                value={notifyEmail}
                onChange={e => setNotifyEmail(e.target.value)}
                placeholder="patient@email.com"
              />
            </div>

            <div className="ar-notify-field">
              <label>Custom Message (optional)</label>
              <textarea
                value={notifyMessage}
                onChange={e => setNotifyMessage(e.target.value)}
                placeholder="Leave blank for default reminder message..."
                rows={4}
              />
            </div>

            <div className="modal-actions">
              <button className="btn" onClick={() => setShowNotifyModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSendNotification}
                disabled={notifySending || !notifyEmail}
              >
                {notifySending ? 'Sending...' : 'Send Notification'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ARPage;
